import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import api from "@/lib/api";

export function MapSection() {
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  const futures = [
    { x: 30, y: 58 }, { x: 62, y: 24 }, { x: 40, y: 72 },
  ];

  return (
    <section className="relative py-28 sm:py-36 bg-[#0B0D13]" data-testid="map-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid gap-14 lg:grid-cols-[0.85fr_1.15fr] items-center">
        <div>
          <SectionHeading eyebrow="Maillage territorial" lines={["Construire", "un réseau national."]} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base sm:text-lg text-[var(--txt-2)] font-light leading-relaxed max-w-md">
              Notre ambition est de développer progressivement un réseau d'entreprises complémentaires en France et en
              Suisse. La carte évoluera au rythme des intégrations du groupe.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex gap-8">
              <div>
                <div className="font-serif text-4xl text-white">02</div>
                <div className="eyebrow mt-1 !text-slate-400">Pôles opérationnels</div>
              </div>
              <div>
                <div className="font-serif text-4xl text-gold">∞</div>
                <div className="eyebrow mt-1 !text-slate-400">Futures implantations</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex items-center gap-5 text-xs text-slate-400">
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[var(--gold)]" /> Opérationnel</span>
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-dashed border-white/40" /> À venir</span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="relative aspect-[4/3] w-full rounded-2xl border border-white/10 bg-[#0F1118] overflow-hidden" data-testid="map-canvas">
            {/* topographic grid */}
            <div
              className="absolute inset-0 opacity-[0.12]"
              style={{ backgroundImage: "linear-gradient(rgba(212,175,55,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(212,175,55,0.5) 1px,transparent 1px)", backgroundSize: "44px 44px" }}
            />
            <div className="absolute inset-0 radial-gold" />
            {/* region labels */}
            <span className="absolute left-[30%] top-[46%] -translate-x-1/2 font-serif text-2xl text-white/15">FRANCE</span>
            <span className="absolute left-[68%] top-[26%] -translate-x-1/2 font-serif text-xl text-white/15">SUISSE</span>

            {/* future markers */}
            {futures.map((f, i) => (
              <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${f.x}%`, top: `${f.y}%` }}>
                <div className="h-3 w-3 rounded-full border border-dashed border-white/40" />
              </div>
            ))}

            {/* operational hubs */}
            {locations.map((l, i) => (
              <motion.div
                key={l.id || i}
                className="absolute -translate-x-1/2 -translate-y-1/2 group"
                style={{ left: `${l.x}%`, top: `${l.y}%` }}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.15, type: "spring" }}
                data-testid={`map-point-${i}`}
              >
                <span className="absolute inset-0 -m-3 rounded-full bg-[var(--gold)]/20 animate-ping" />
                <span className="relative block h-3.5 w-3.5 rounded-full bg-[var(--gold)] shadow-[0_0_16px_rgba(212,175,55,0.8)]" />
                <div className="absolute left-1/2 top-5 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#07080B]/90 border border-white/10 px-2.5 py-1 text-[11px] text-white">
                  <span className="text-gold font-medium">{l.name}</span>
                  <span className="text-slate-400"> · {l.note}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
